import { useContext } from 'react';
import { Box, Flex, HStack, useBreakpointValue } from '@chakra-ui/react';
import type { User } from '@/models';
import { FirebaseContext } from '@/session/FirebaseContext';
import { useUserProfile } from '@/session/useUserProfile';
import { SiteNavLinks } from './SiteNavLinks';
import { useSiteLogoClick } from './useSiteLogoClick';
import { SiteNavLogo } from './SiteNavLogo';
import { useSiteNavBarStyles } from './siteNavBarStyles';
import { siteNavShellProps } from './siteNavLayout';
import { AccountSettingsModals, useAccountSettingsDisclosures } from './AccountSettingsModals';
import { ColorModeToggle } from '@/components/ColorModeToggle';
import { SiteNavAccountMenu } from './SiteNavAccountMenu';
import { SiteNavAuthButtons } from './SiteNavAuthButtons';
import { SiteNavMobileMenu } from './SiteNavMobileMenu';
import { useSiteNavCompact } from './useSiteNavCompact';

/** App-wide top navigation (logo, tabs, login or account menu). */
export function SiteNav(props: { userData?: User }) {
  const ctx = useContext(FirebaseContext);
  const profileFromProvider = useUserProfile().userData;
  const userData = props.userData ?? profileFromProvider;
  const onLogoClick = useSiteLogoClick();
  const {
    editNameDisclosure,
    deleteAccountDisclosure,
    setPasswordDisclosure,
    changePasswordDisclosure,
    reauthDisclosure,
  } = useAccountSettingsDisclosures();
  const navPaddingL = useBreakpointValue({ base: 4, lg: 8 });
  const navPaddingR = useBreakpointValue({ base: 4, lg: 8 });
  const navBarStyles = useSiteNavBarStyles();
  const { compact, linksSlotRef, linksMeasureRef } = useSiteNavCompact();

  const navUserData: User | undefined =
    userData ??
    (ctx.user
      ? {
          admin: false,
          name: ctx.user.displayName || ctx.user.email?.split('@')[0] || 'Account',
          email: ctx.user.email ?? '',
          shareShortLink: null,
          requiresShareUpdate: false,
        }
      : undefined);

  const showAccountMenu = Boolean(ctx.user && navUserData);

  return (
    <>
      <Box w='full' {...siteNavShellProps} {...navBarStyles}>
        <Box maxW='1444px' w='full' mx='auto' py={2} pl={navPaddingL} pr={navPaddingR}>
          <Flex minH={16} h={16} alignItems='center' justifyContent='space-between' gap={2}>
            <HStack spacing={{ base: 2, lg: 6 }} minW={0} flex={1} alignItems='center'>
              <SiteNavLogo
                onClick={onLogoClick}
                ariaLabel={ctx.user ? 'Go to list and leagues' : 'Go to rules'}
              />
              {/* Always mounted so ResizeObserver can measure intrinsic link width at lg+. */}
              <Box ref={linksSlotRef} minW={0} flex={1} position='relative' overflow='hidden'>
                <Box
                  ref={linksMeasureRef}
                  visibility={compact ? 'hidden' : 'visible'}
                  pointerEvents={compact ? 'none' : 'auto'}
                  position={compact ? 'absolute' : 'relative'}
                  left={0}
                  top={compact ? '50%' : undefined}
                  transform={compact ? 'translateY(-50%)' : undefined}
                  whiteSpace='nowrap'
                  w='max-content'
                  maxW='none'
                >
                  <SiteNavLinks />
                </Box>
              </Box>
            </HStack>

            <HStack justifyContent='end' alignItems='center' height='full' flexShrink={0} spacing={1}>
              <ColorModeToggle />
              <SiteNavMobileMenu
                compact={compact}
                openEditName={editNameDisclosure.onOpen}
                openDeleteAccount={deleteAccountDisclosure.onOpen}
                openSetPassword={setPasswordDisclosure.onOpen}
                openChangePassword={changePasswordDisclosure.onOpen}
              />
              {showAccountMenu ? (
                <SiteNavAccountMenu
                  userData={navUserData!}
                  visible={!compact}
                  openEditName={editNameDisclosure.onOpen}
                  openDeleteAccount={deleteAccountDisclosure.onOpen}
                  openSetPassword={setPasswordDisclosure.onOpen}
                  openChangePassword={changePasswordDisclosure.onOpen}
                />
              ) : (
                <SiteNavAuthButtons visible={!compact} />
              )}
            </HStack>
          </Flex>
        </Box>
      </Box>
      {showAccountMenu ? (
        <AccountSettingsModals
          userData={navUserData!}
          editNameDisclosure={editNameDisclosure}
          deleteAccountDisclosure={deleteAccountDisclosure}
          setPasswordDisclosure={setPasswordDisclosure}
          changePasswordDisclosure={changePasswordDisclosure}
          reauthDisclosure={reauthDisclosure}
        />
      ) : null}
    </>
  );
}
